/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useEventsData } from "./hooks";
import CustomDataGrid from "../../../Shared/Table/CustomDataGrid";
import { IEventConfig } from "../../../../store/api/deviceConfig/types";
import { RadixSelectControl } from "../../../ui/radix-select";
import { Button } from "../../../ui/button";
import { Sheet } from "../../../ui/sheet";
import SearchIcon from "@mui/icons-material/Search";
import { updateKeywordForScope, resetKeywordForScope } from "../../../../store/search/reducer";
import { searchKeywordByScopeSelector } from "../../../../store/search/selector";

const Events: FC = () => {
    const dispatch = useDispatch();
    const searchText = useSelector(searchKeywordByScopeSelector("eventConfiguration"));
    const [bulkSnmp, setBulkSnmp] = React.useState("");
    const [bulkEmail, setBulkEmail] = React.useState("");
    const {
        filterValue,
        configData,
        configStatus,
        isLoading,
        eventsConfigurationColumns,
        selectSNMPProps,
        selectEmailProps,
        severityProps,
        handleOnSelect,
        applyToAllHandler,
        applyToSelectedHandler,
        submitHandler,
        cancelHandler,
        categoryFilter,
        setCategoryFilter,
        categoryOptions,
        selectedRows,
        setSelectedRows,
        editSheetOpen,
        editingRow,
        editFormValues,
        setEditFormValues,
        handleEditClose,
        handleEditSave,
    } = useEventsData();

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (!val) {
            dispatch(resetKeywordForScope({ scope: "eventConfiguration" }));
        } else {
            dispatch(updateKeywordForScope({ scope: "eventConfiguration", keyword: val }));
        }
    };

    React.useEffect(() => {
        if (!selectedRows.length) {
            setBulkSnmp("");
            setBulkEmail("");
        }
    }, [selectedRows.length]);

    const handleBulkApply = () => {
        applyToSelectedHandler({ snmp: bulkSnmp, email: bulkEmail });
    };

    return (
        <div className="pt-2 pl-4">
            {selectedRows.length > 0 ? (
                /* ── Selection action bar ── */
                <div
                    role="toolbar"
                    aria-label="Bulk update forwarding for selected events"
                    className="mb-4 rounded-lg border border-border bg-muted/50 p-4 pt-1 shadow-sm"
                    style={{ backgroundColor: "var(--left-Navigation-background)"}}
                >
                    <p className="text-sm text-muted-foreground">
                        <span className="font-medium tabular-nums text-foreground">{selectedRows.length}</span>{" "}
                        {selectedRows.length === 1 ? "event" : "events"} selected - choose forwarding and apply to the selection.
                    </p>
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-nowrap sm:items-end sm:gap-4">
                        <div className="min-w-0 flex-1 space-y-1.5 sm:max-w-[220px]">
                            <span className="text-xs font-medium text-muted-foreground">Forward to SNMP</span>
                            <RadixSelectControl
                                value={bulkSnmp || '__placeholder__'}
                                onValueChange={(val) => {
                                    const selectedValue = val === '__placeholder__' ? '' : val;
                                    setBulkSnmp(selectedValue);
                                }}
                                options={[
                                    { value: '__placeholder__', label: "Select SNMP" },
                                    ...selectSNMPProps.selectOptions.map((item: string) => ({ value: item, label: item }))
                                ]}
                                className={`capitalize-text h-9 w-full min-w-0 ${bulkSnmp ? "focus:ring-1 focus:ring-border/60 focus:border-blue-300 focus-visible:ring-1 focus-visible:ring-border/60 focus-visible:border-blue-300 data-[state=open]:ring-1 data-[state=open]:ring-border/60 data-[state=open]:border-blue-300" : ""}`}
                                placeholder="Select SNMP"
                                contentWidth="220px"
                            />
                        </div>

                        <div className="min-w-0 flex-1 space-y-1.5 sm:max-w-[220px]">
                            <span className="text-xs font-medium text-muted-foreground">Forward to Email</span>
                            <RadixSelectControl
                                value={bulkEmail || '__placeholder__'}
                                onValueChange={(val) => {
                                    const selectedValue = val === '__placeholder__' ? '' : val;
                                    setBulkEmail(selectedValue);
                                }}
                                options={[
                                    { value: '__placeholder__', label: "Select Email" },
                                    ...selectEmailProps.selectOptions.map((item: string) => ({ value: item, label: item }))
                                ]}
                                className={`capitalize-text h-9 w-full min-w-0 ${bulkEmail ? "focus:ring-1 focus:ring-border/60 focus:border-blue-300 focus-visible:ring-1 focus-visible:ring-border/60 focus-visible:border-blue-300 data-[state=open]:ring-1 data-[state=open]:ring-border/60 data-[state=open]:border-blue-300" : ""}`}
                                placeholder="Select Email"
                                contentWidth="220px"
                            />
                        </div>

                        <div className="flex shrink-0 justify-end gap-2 sm:ml-auto">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={(event) => {
                                    setBulkSnmp("");
                                    setBulkEmail("");
                                    setSelectedRows([]);
                                    cancelHandler();
                                }}
                                disabled={configStatus.isLoading || isLoading["statusEventConfig"]}
                            >
                                Cancel
                            </Button>
                            <Button type="button" size="sm" onClick={handleBulkApply} disabled={configStatus.isLoading || isLoading["statusEventConfig"]}>
                                {isLoading["statusEventConfig"] && <span className="animate-spin inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full mr-1" />}
                                Apply to selected
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                /* ── Normal filters toolbar ── */
                <div>
                    <div className="flex items-center gap-4 pb-3">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'var(--background)',
                                border: '1px solid var(--input)',
                                borderRadius: 6,
                                padding: '0 12px',
                                minWidth: 280,
                                height: 36,
                            }}
                        >
                            <SearchIcon style={{ color: 'var(--muted-foreground)', fontSize: 18, marginRight: 4 }} />
                            <input
                                type="text"
                                value={searchText}
                                onChange={handleSearchChange}
                                placeholder="Search events..."
                                style={{
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    fontSize: 14,
                                    color: 'var(--foreground)',
                                    width: '100%',
                                    height: '100%',
                                }}
                            />
                        </div>
                        <RadixSelectControl
                            value={categoryFilter || '__placeholder__'}
                            onValueChange={(val) => setCategoryFilter(val === '__placeholder__' ? '' : val)}
                            options={[
                                { value: '__placeholder__', label: "Category" },
                                ...categoryOptions.map((cat: string) => ({ value: cat, label: cat }))
                            ]}
                            className="capitalize-text w-28 max-w-[8rem]"
                            placeholder="Category"
                            contentWidth="140px"
                        />
                        <RadixSelectControl
                            value={filterValue.severity || '__placeholder__'}
                            onValueChange={(val) => handleOnSelect({ target: { name: "severity", value: val === '__placeholder__' ? '' : val } } as any)}
                            options={[
                                { value: '__placeholder__', label: "Severity" },
                                ...severityProps.selectOptions.map((item: string) => ({ value: item, label: item }))
                            ]}
                            className="capitalize-text w-40 max-w-[8rem]"
                            placeholder="Severity"
                            contentWidth="210px"
                        />
                        <RadixSelectControl
                            value={filterValue.snmp || '__placeholder__'}
                            onValueChange={(val) => handleOnSelect({ target: { name: "snmp", value: val === '__placeholder__' ? '' : val } } as any)}
                            options={[
                                { value: '__placeholder__', label: "SNMP" },
                                ...selectSNMPProps.selectOptions.map((item: string) => ({ value: item, label: item }))
                            ]}
                            className="capitalize-text w-28 max-w-[8rem]"
                            placeholder="SNMP"
                            contentWidth="140px"
                        />
                        <RadixSelectControl
                            value={filterValue.email || '__placeholder__'}
                            onValueChange={(val) => handleOnSelect({ target: { name: "email", value: val === '__placeholder__' ? '' : val } } as any)}
                            options={[
                                { value: '__placeholder__', label: "Email" },
                                ...selectEmailProps.selectOptions.map((item: string) => ({ value: item, label: item }))
                            ]}
                            className="capitalize-text w-40 max-w-[8rem]"
                            placeholder="Email"
                            contentWidth="210px"
                        />
                        {/* <Button variant="secondary" size="sm" onClick={applyToAllHandler} disabled={configStatus.isLoading || isLoading["statusEventConfig"]}>
                            Apply to All
                        </Button>
                        <Button variant="secondary" size="sm" onClick={cancelHandler} disabled={configStatus.isLoading || isLoading["statusEventConfig"]}>
                            Cancel
                        </Button>
                        <Button variant="secondary" size="sm" onClick={submitHandler} disabled={configStatus.isLoading || isLoading["statusEventConfig"]}>
                            {isLoading["statusEventConfig"] && <span className="animate-spin inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full" />}
                            Save
                        </Button> */}
                    </div>
                </div>
            )}
            <div className="pt-2">
                <CustomDataGrid
                    rows={configData}
                    columns={eventsConfigurationColumns}
                    tableId="eventConfiguration"
                    rowCount={configData.length}
                    loading={configStatus.isLoading}
                    disableColumnMenu={true}
                    autoHeight={false}
                    disableRowSelectionOnClick={true}
                    getRowId={(row: IEventConfig) => row.key}
                    paginationMode="client"
                    sortingMode="client"
                    checkboxSelection={true}
                    rowSelectionModel={selectedRows}
                    onRowSelectionModelChange={(model) => setSelectedRows(model as string[])}
                    sx={{
                        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                            outline: "none",
                        },
                    }}
                    fillContainer
                />
            </div>

            <Sheet
                open={editSheetOpen}
                onClose={handleEditClose}
                title="Edit Event"
                subtitle={editingRow?.displayName}
                width="440px"
                footer={
                    <>
                        <Button variant="outline" onClick={handleEditClose}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleEditSave}
                            disabled={isLoading["statusEventConfig"]}
                        >
                            {isLoading["statusEventConfig"] && (
                                <span className="animate-spin inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full mr-1" />
                            )}
                            Save
                        </Button>
                    </>
                }
            >
                <div className="space-y-5">
                    {/* Event — read-only */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-foreground">Event</label>
                        <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                            {editingRow?.displayName || "—"}
                        </div>
                    </div>

                    {/* Category — read-only */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-foreground">Category</label>
                        <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                            {editingRow?.category || "—"}
                        </div>
                    </div>

                    {/* Severity */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-foreground">Severity</label>
                        <RadixSelectControl
                            value={editFormValues.severity || "__placeholder__"}
                            onValueChange={(val) =>
                                setEditFormValues((prev) => ({ ...prev, severity: val === "__placeholder__" ? "" : val }))
                            }
                            options={[
                                { value: "__placeholder__", label: "Select Severity" },
                                ...severityProps.selectOptions.map((item: string) => ({ value: item, label: item })),
                            ]}
                            placeholder="Select Severity"
                            contentWidth="200px"
                            className="w-full"
                        />
                    </div>

                    {/* Forward To SNMP */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-foreground">Forward To SNMP</label>
                        <RadixSelectControl
                            value={editFormValues.snmpManagerGroup || "__placeholder__"}
                            onValueChange={(val) =>
                                setEditFormValues((prev) => ({ ...prev, snmpManagerGroup: val === "__placeholder__" ? "" : val }))
                            }
                            options={[
                                { value: "__placeholder__", label: "Select SNMP group" },
                                ...selectSNMPProps.selectOptions.map((item: string) => ({ value: item, label: item })),
                            ]}
                            placeholder="Select SNMP group"
                            contentWidth="240px"
                            className="w-full"
                        />
                    </div>

                    {/* Forward To Email */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-foreground">Forward To Email</label>
                        <RadixSelectControl
                            value={editFormValues.emailGroup || "__placeholder__"}
                            onValueChange={(val) =>
                                setEditFormValues((prev) => ({ ...prev, emailGroup: val === "__placeholder__" ? "" : val }))
                            }
                            options={[
                                { value: "__placeholder__", label: "Select Email group" },
                                ...selectEmailProps.selectOptions.map((item: string) => ({ value: item, label: item })),
                            ]}
                            placeholder="Select Email group"
                            contentWidth="240px"
                            className="w-full"
                        />
                    </div>
                </div>
            </Sheet>
        </div>
    );
};

export default Events;